import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import {
  Output,
  Component,
  OnDestroy,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation/helpers';
import { SnUserFieldsGeneratorService } from '@lib/users';

import { ActivateService, IConfirmPhoneResponse, AuthActivateStepStatus } from '../activate';

const VERIFICATION_CODE_REGEXP = /^\d{6}$/;

@Component({
  selector: 'sn-auth-confirm-phone',
  templateUrl: './confirm-phone.container.html',
  styleUrls: ['./confirm-phone.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmPhoneContainer implements OnDestroy {
  @Output()
  public readonly confirmed = new EventEmitter<IConfirmPhoneResponse>();

  public readonly form = new UntypedFormGroup({});
  public readonly formFields: SnFormFieldConfig[] = [
    this._userFieldsGeneratorService.getVerificationCodeField({
      className: 'verification-code',
      templateOptions: {
        required: true,
        pattern: VERIFICATION_CODE_REGEXP,
        attributes: {
          autocomplete: 'nope',
        },
      },
      validators: {
        server: (control) => this._serverErrorHandler(control),
      },
      validation: {
        messages: {
          pattern: 'Wrong number of digits',
          server: () => this._verificationCodeServerError,
        },
      },
    }),
  ];

  public formModel: any = {};

  public label: string;
  public status: AuthActivateStepStatus;

  private _verificationCodeServerError: string;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _userFieldsGeneratorService: SnUserFieldsGeneratorService,
    private readonly _activateService: ActivateService,
  ) {
    this._updateStatus(AuthActivateStepStatus.WAIT);
  }

  get isLoading(): boolean {
    return this.status === AuthActivateStepStatus.PROGRESS;
  }

  get phone$(): Observable<string> {
    return this._activateService.mobilePhone$;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public confirmPhone(): void {
    if (this.form.invalid) {
      this._updateStatus(AuthActivateStepStatus.ERROR);

      return;
    }

    this._updateStatus(AuthActivateStepStatus.PROGRESS);

    this._activateService
      .confirmPhone(this.form.value.verificationCode)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this._changeDetectorRef.markForCheck()),
      )
      .subscribe({
        next: (response) => {
          this._updateStatus(AuthActivateStepStatus.SUCCESS);
          this.confirmed.emit(response);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this._updateStatus(AuthActivateStepStatus.ERROR);
          this._verificationCodeServerError = errorResponse.error.verificationCode;
          this.form.get('verificationCode').updateValueAndValidity();
        },
      });
  }

  private _serverErrorHandler(control: AbstractControl): boolean {
    if (control.hasError('server')) {
      this._verificationCodeServerError = null;
    }

    return !this._verificationCodeServerError;
  }

  private _updateStatus(status: AuthActivateStepStatus): void {
    this.status = status;

    switch (status) {
      case AuthActivateStepStatus.PROGRESS:
        this.label = t('auth.confirmPhone.status.progress');
        break;

      case AuthActivateStepStatus.SUCCESS:
        this.label = t('auth.confirmPhone.status.success');
        break;

      default:
        this.label = t('auth.confirmPhone.status.default');
    }
  }
}
